import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { invoicesAPI, projectsAPI, usersAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Trash2, Pencil, Loader2, FileText, Search, DollarSign, Eye, Download } from 'lucide-react';
import { generateInvoicePDF } from '../lib/pdfExport';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  paid: 'bg-emerald-100 text-emerald-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-yellow-100 text-yellow-800',
};

export default function Invoices() {
  const { isAdmin, isStaff, isClient } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const makeLineItem = () => ({ _uid: Date.now().toString() + Math.random().toString(36).slice(2), description: '', quantity: 1, amount: '' });
  const emptyForm = {
    invoice_number: '', project_id: '', client_id: '', client_name: '', items: [makeLineItem()],
    tax_rate: 0, due_date: '', notes: '', status: 'draft'
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invRes, projRes] = await Promise.all([invoicesAPI.list(), projectsAPI.list()]);
        setInvoices(invRes.data);
        setProjects(projRes.data);
        if (canEdit) {
          const usersRes = await usersAPI.list();
          setClients(usersRes.data.filter(u => u.role === 'client'));
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [canEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        ...form,
        items: form.items.filter(i => i.description.trim()),
        tax_rate: parseFloat(form.tax_rate) || 0,
      };
      if (selectedInvoice) {
        await invoicesAPI.update(selectedInvoice.invoice_id, payload);
      } else {
        await invoicesAPI.create(payload);
      }
      const res = await invoicesAPI.list();
      setInvoices(res.data);
      setDialogOpen(false);
      setSelectedInvoice(null);
      setForm(emptyForm);
    } catch (err) {
      console.error('Failed to save invoice:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    try {
      await invoicesAPI.delete(invoiceId);
      setInvoices(invoices.filter(i => i.invoice_id !== invoiceId));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await invoicesAPI.update(invoiceId, { status: newStatus });
      const res = await invoicesAPI.list();
      setInvoices(res.data);
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const openEdit = (inv) => {
    setSelectedInvoice(inv);
    setForm({
      invoice_number: inv.invoice_number, project_id: inv.project_id || '', client_id: inv.client_id || '',
      client_name: inv.client_name || '', items: inv.items?.length > 0 ? inv.items.map((item, i) => ({ ...item, _uid: item._uid || `${Date.now()}-${i}` })) : [makeLineItem()],
      tax_rate: inv.tax_rate || 0, due_date: inv.due_date ? inv.due_date.split('T')[0] : '', notes: inv.notes || '', status: inv.status
    });
    setDialogOpen(true);
  };

  const addLineItem = () => setForm({ ...form, items: [...form.items, makeLineItem()] });
  const removeLineItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateLineItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };

  const subtotal = form.items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0) * (parseInt(i.quantity) || 1), 0);
  const tax = subtotal * (parseFloat(form.tax_rate) || 0) / 100;
  const total = subtotal + tax;

  const filtered = invoices.filter(inv => {
    const matchSearch = inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const projectMap = Object.fromEntries(projects.map(p => [p.project_id, p.name]));

  // Summary stats
  const totalRevenue = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const totalOutstanding = invoices.filter(i => ['draft', 'sent'].includes(i.status)).reduce((s, i) => s + (i.total || 0), 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="invoices-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Invoices</h1>
            <p className="text-sm text-muted-foreground">Manage billing and payments</p>
          </div>
          {canEdit && (
            <Button onClick={() => { setForm(emptyForm); setSelectedInvoice(null); setDialogOpen(true); }} data-testid="create-invoice-btn">
              <Plus className="h-4 w-4 mr-2" /> New Invoice
            </Button>
          )}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground">Total Invoices</p><p className="text-xl font-bold">{invoices.length}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground">Paid</p><p className="text-xl font-bold text-emerald-600">{invoices.filter(i => i.status === 'paid').length}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground">Revenue</p><p className="text-xl font-bold">{totalRevenue.toLocaleString('en-IN', {style:'currency',currency:'INR',maximumFractionDigits:0})}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground">Outstanding</p><p className="text-xl font-bold text-amber-600">{totalOutstanding.toLocaleString('en-IN', {style:'currency',currency:'INR',maximumFractionDigits:0})}</p></CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search invoices..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="invoice-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="animate-pulse space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-muted rounded" />)}</div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No invoices found</p></CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(inv => (
                    <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                      <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                      <TableCell>{inv.client_name || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{projectMap[inv.project_id] || '—'}</TableCell>
                      <TableCell className="text-right font-medium">{(inv.total || 0).toLocaleString('en-IN', {style:'currency',currency:'INR',maximumFractionDigits:0})}</TableCell>
                      <TableCell className="text-sm">{inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'}) : '—'}</TableCell>
                      <TableCell>
                        {canEdit ? (
                          <Select value={inv.status} onValueChange={v => handleStatusChange(inv.invoice_id, v)}>
                            <SelectTrigger className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {['draft','sent','paid','overdue','cancelled'].map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_STYLES[inv.status] || STATUS_STYLES.draft}`}>{inv.status}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelectedInvoice(inv); setViewOpen(true); }} data-testid={`view-invoice-${inv.invoice_id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => generateInvoicePDF(inv)} title="Export PDF" data-testid={`pdf-invoice-${inv.invoice_id}`}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {canEdit && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(inv)}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(inv.invoice_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedInvoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle>
              <DialogDescription>Fill in the invoice details below</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Invoice Number</Label>
                    <Input value={form.invoice_number} onChange={e => setForm({...form, invoice_number: e.target.value})} placeholder="Auto-generated if empty" data-testid="invoice-number-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} data-testid="invoice-due-date" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Project</Label>
                    <Select value={form.project_id || 'none'} onValueChange={v => setForm({...form, project_id: v === 'none' ? '' : v})}>
                      <SelectTrigger data-testid="invoice-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <Select value={form.client_id || 'none'} onValueChange={v => {
                      const client = clients.find(c => c.user_id === v);
                      setForm({...form, client_id: v === 'none' ? '' : v, client_name: client?.name || ''});
                    }}>
                      <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Client</SelectItem>
                        {clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  <Label>Line Items</Label>
                  <div className="space-y-2">
                    {form.items.map((item, idx) => (
                      <div key={item._uid} className="flex gap-2 items-start">
                        <Input className="flex-1" placeholder="Description" value={item.description} onChange={e => updateLineItem(idx, 'description', e.target.value)} data-testid={`line-item-desc-${idx}`} />
                        <Input className="w-20" type="number" min="1" placeholder="Qty" value={item.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} />
                        <Input className="w-28" type="number" min="0" step="0.01" placeholder="Amount" value={item.amount} onChange={e => updateLineItem(idx, 'amount', e.target.value)} data-testid={`line-item-amount-${idx}`} />
                        {form.items.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => removeLineItem(idx)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Line
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tax Rate (%)</Label>
                    <Input type="number" min="0" max="100" step="0.1" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: e.target.value})} />
                  </div>
                  <div className="space-y-2 text-right pt-6">
                    <p className="text-sm text-muted-foreground">Subtotal: {subtotal.toLocaleString('en-IN', {style:'currency',currency:'INR'})}</p>
                    {tax > 0 && <p className="text-sm text-muted-foreground">Tax: {tax.toLocaleString('en-IN', {style:'currency',currency:'INR'})}</p>}
                    <p className="text-lg font-bold">Total: {total.toLocaleString('en-IN', {style:'currency',currency:'INR'})}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes..." />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} data-testid="save-invoice-btn">
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {selectedInvoice ? 'Update' : 'Create Invoice'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Invoice Dialog */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Invoice {selectedInvoice?.invoice_number}</DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4 py-2" data-testid="invoice-view">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-muted-foreground">Client</p><p className="font-medium">{selectedInvoice.client_name || '—'}</p></div>
                  <div><p className="text-muted-foreground">Project</p><p className="font-medium">{projectMap[selectedInvoice.project_id] || '—'}</p></div>
                  <div><p className="text-muted-foreground">Status</p><p><span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_STYLES[selectedInvoice.status]}`}>{selectedInvoice.status}</span></p></div>
                  <div><p className="text-muted-foreground">Due Date</p><p className="font-medium">{selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString('en-IN') : '—'}</p></div>
                </div>
                <div className="border-t pt-3">
                  <p className="text-sm font-medium mb-2">Items</p>
                  {(selectedInvoice.items || []).map((item, idx) => (
                    <div key={`${item.description}-${item.amount}-${idx}`} className="flex justify-between text-sm py-1 border-b last:border-0">
                      <span>{item.description} x{item.quantity || 1}</span>
                      <span className="font-medium">{((parseFloat(item.amount) || 0) * (parseInt(item.quantity) || 1)).toLocaleString('en-IN', {style:'currency',currency:'INR'})}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-3 text-right space-y-1">
                  <p className="text-sm">Subtotal: {(selectedInvoice.subtotal || 0).toLocaleString('en-IN', {style:'currency',currency:'INR'})}</p>
                  {selectedInvoice.tax > 0 && <p className="text-sm">Tax ({selectedInvoice.tax_rate}%): {selectedInvoice.tax.toLocaleString('en-IN', {style:'currency',currency:'INR'})}</p>}
                  <p className="text-lg font-bold">Total: {(selectedInvoice.total || 0).toLocaleString('en-IN', {style:'currency',currency:'INR'})}</p>
                </div>
                {selectedInvoice.notes && <p className="text-sm text-muted-foreground border-t pt-3">{selectedInvoice.notes}</p>}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
